import _jsx from "@babel/runtime/helpers/jsx";
var _span;
/* global CNPC */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { injectScript } from '@atc/dom-fns';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import OwnerChatCta from '../OwnerChatCta/index.js';

/**
 * CSS selectors for CarNow chat button elements that need to be managed.
 * @constant {string[]} cnButtons
 */
const cnButtons = ['#cn_chat_container', '#cn_tracking', '#cn_poke_wig', '.cn-wid-cont'];

/**
 * Renders the chat button for CarNow with different styling based on UI context.
 * @param {object} props - The component props
 * @param {string} props.className - CSS class name to add to the button
 * @param {string} props.label - Custom label text for the button
 * @param {function} props.onChatClick - Callback function for clicking the chat button
 * @param {string} props.uiContext - UI context to render different styles of button ('iconButton', 'iconButtonStacked', 'button', 'link')
 * @returns {JSX.Element} The rendered chat button component
 */
export const renderButton = _ref => {
  let {
    className,
    label,
    onChatClick,
    uiContext,
    ...props
  } = _ref;
  const handleButtonClick = () => {
    onChatClick();
    if (typeof window?.CarNowPlugin !== 'undefined') {
      window.CarNowPlugin.launchChat();
    }
  };

  // delete invalid dom attributes before spreading
  delete props.merchantId;
  return /*#__PURE__*/React.createElement(OwnerChatCta, Object.assign({
    className: className,
    label: label,
    onChatClick: handleButtonClick,
    uiContext: uiContext
  }, props));
};

/**
 * Do not remove or destroy anything from the CarNowPlugin. It will corrupt it.
 *
 * This function disables the CarNow chat, cancels poke checks, closes dialogs,
 * and hides all CarNow-related UI elements.
 */
const closeCarNowChat = () => {
  window.carNowChatEnabled = false;
  CNPC?.cancelPokeCheck();
  CNPC?.closePokeDialog();
  cnButtons.forEach(selector => {
    const element = document.querySelector(selector);
    if (selector === '#cn_chat_container' && !element?.classList?.contains('cn-minimized') && element?.classList?.contains('cn-chat-container')) {
      CNPC?.toggleChat();
    } else {
      CNPC?.closeWidget(selector);
    }
  });
};

/**
 * Shows the CarNow chat widgets and enables chat functionality.
 * This function makes all CarNow chat elements visible and sets the chat as enabled.
 */
const showCarNowChat = () => {
  if (window?.CarNowPlugin && !window.carNowChatEnabled) {
    cnButtons.forEach(selector => {
      window?.CNJQ?.(selector)?.show();
    });
    window.carNowChatEnabled = true;
  }
};

/**
 * OwnerChatCarNow Component
 *
 * A React component that integrates with the CarNow chat system for vehicle listings.
 * It handles CarNow script injection, chat initialization, and manages the visibility
 * and state of CarNow chat widgets.
 *
 * @component
 * @param {object} props - The component props
 * @param {string} [props.className] - CSS class name to apply to the chat button
 * @param {boolean} [props.showChatCTA=true] - Whether to show the chat call-to-action button
 * @param {function} props.onChatClick - Callback function triggered when chat button is clicked
 * @param {string} [props.uiContext] - UI context for button styling ('iconButton', 'iconButtonStacked', 'button', 'link')
 * @returns {JSX.Element} The rendered component
 */
const OwnerChatCarNow = /*#__PURE__*/React.memo(_ref2 => {
  let {
    className,
    showChatCTA,
    onChatClick,
    uiContext,
    ...rest
  } = _ref2;
  const [isScriptInjected, setIsScriptInjected] = useState(false);
  const carNowUrl = useSelector(state => _get(state, 'configs.carNowUrl', ''), _isEqual);

  // unmount with empty dependency array. this will call exactly one time
  useEffect(() => () => {
    if (window?.CarNowPlugin && window?.CNPC) {
      closeCarNowChat();
    }
  }, []);

  // inject the CN Script when the first time load the page
  useEffect(() => {
    if (!isScriptInjected) {
      // check to make sure this cannot inject more than one time
      if (!document.querySelector(`script[src="${carNowUrl}"]`)) {
        injectScript(carNowUrl).then(() => {
          setIsScriptInjected(true);

          // eslint-disable-next-line no-console
          window.beforeCNPluginLoad = () => console.info('Start load CNPlugin');
          // eslint-disable-next-line no-console
          window.onCNPluginLoad = () => console.info('Load CNPlugin completed');
          window._sendCustomAnalyticEvent = window?.carNowChatEventCallBack || window._sendCustomAnalyticEvent;
          window.CarNowPlugin.init();
        }).catch(error => {
          // eslint-disable-next-line no-console
          console.error('Inject CN script failed with detail: ', error?.message);
        });
      } else {
        setIsScriptInjected(true);
      }
    }
  }, [carNowUrl, isScriptInjected]);

  // Show the CN chat buttons. This will not run repeatedly when this component isn't destroyed
  useEffect(() => {
    if (isScriptInjected && showChatCTA) {
      showCarNowChat();
    }
  }, [isScriptInjected, showChatCTA]);
  return /*#__PURE__*/_jsx("div", {
    id: "owner-chat-container-carnow",
    className: uiContext === 'snapshot' ? 'display-flex' : 'display-inline display-sm-block'
  }, void 0, showChatCTA && isScriptInjected ? renderButton({
    className,
    onChatClick,
    uiContext,
    ...rest
  }) : _span || (_span = /*#__PURE__*/_jsx("span", {})));
});
export default OwnerChatCarNow;